import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Radio,
  Tornado,
  Wind,
  CloudHail,
  Siren,
  ShieldAlert,
  Eye,
  Clock,
  MapPin,
  Navigation,
  Zap,
  ExternalLink,
} from 'lucide-react'
import { useStormEscalation } from './useStormEscalation'
import { useLiveEta } from './useLiveEta'
import { useCountdown } from './useCountdown'
import { RadarMap } from './RadarMap'
import { SkyTint } from '@/features/home/SkyTint'
import { parseMotion, compass, type StormMotion } from '@/lib/storm/motion'
import { parseHazards, hailLabel } from '@/lib/storm/hazards'
import { pointInGeometry, type Eta } from '@/lib/storm/geo'
import { warningTitle, shelterAction, polygonHex } from '@/lib/storm/copy'
import { damageClass } from '@/lib/storm/format'
import './storm.css'

/**
 * STORM MODE — the red war-room. Auto-entered (via the AppShell watcher) when a
 * STORM-MODE-worthy NWS warning is active for the user's location; also reachable
 * at /storm and via ?demo=storm|tornado. Full-bleed red (SkyTint, parity with the
 * home transform): live radar + the real warning polygon + a live impact countdown,
 * distance + motion, severity-scaled IBW tags, expiry countdown, and shelter copy.
 */
export function StormModeView() {
  const { saved, warning, watch } = useStormEscalation()

  // Hooks must run unconditionally — derive null-safe inputs, then branch in render.
  const motion = parseMotion(warning?.parameters)
  const hazards = parseHazards(warning?.parameters)
  const user = { lat: saved?.lat ?? 0, lon: saved?.lon ?? 0 }
  const inside = !!(saved && warning) && pointInGeometry(saved.lat, saved.lon, warning.geometry)
  const live = useLiveEta(user, motion, warning?.geometry ?? null, inside)
  const expiryCountdown = useCountdown(warning?.ends ?? warning?.expires)
  // A warning takes over Storm Mode, but a broader Tornado / Severe T'storm WATCH usually
  // remains in effect for the wider area — surface it here so it isn't lost behind the warning.
  const watchCountdown = useCountdown(watch?.ends ?? watch?.expires)

  // Full-bleed red wash (or neutral when there's nothing active) — one continuous state
  // home → detail, the same layer the home transform uses.
  const sky = <SkyTint lat={saved?.lat ?? null} lon={saved?.lon ?? null} phase={warning ? 'storm' : 'quiet'} />

  if (!warning || !saved) {
    return (
      <>
        {sky}
        <div className="storm storm--clear">
          <header className="storm__bar">
            <Link to="/" className="storm__back" aria-label="Back home">
              <ChevronLeft size={20} />
            </Link>
            <div className="storm__title">
              <Radio size={16} /> <span>STORM MODE</span>
            </div>
            <span />
          </header>
          <div className="storm__empty">
            <ShieldAlert size={34} />
            <p className="storm__empty-title">No active warning</p>
            <p className="storm__empty-sub">
              Storm Mode opens itself the moment a severe warning is issued for your area.
            </p>
            <Link to="/" className="storm__exit">Back to home</Link>
          </div>
        </div>
      </>
    )
  }

  const polyColor = polygonHex(warning.event)
  const action = shelterAction(warning, hazards)
  const tornadoActive = /tornado/i.test(warning.event) || hazards.tornado != null
  const dmgClass = damageClass(hazards.damageThreat)

  return (
    <>
      {sky}
      <div className="storm storm--mode" style={{ '--c': 'var(--status-severe)' } as React.CSSProperties}>
        <header className="storm__bar">
          <Link to="/" className="storm__back" aria-label="Exit Storm Mode">
            <ChevronLeft size={20} />
          </Link>
          <div className="storm__title">
            <Radio size={16} />
            <span>STORM MODE</span>
          </div>
          {expiryCountdown && (
            <span className="storm__expiry">
              <Clock size={12} /> {expiryCountdown === 'expired' ? 'expired' : `expires ${expiryCountdown}`}
            </span>
          )}
        </header>

        <div className="storm__headline">
          <h1 className="storm__event">{warningTitle(warning.event)}</h1>
          <p className="storm__where">
            <MapPin size={13} /> YOUR AREA · {warning.areaDesc.split(';')[0]}
          </p>
        </div>

        {/* Concurrent WATCH context — a warning is the immediate threat, but the area-wide
            tornado/severe watch is still in effect; surface it (amber) without stealing focus. */}
        {watch && (
          <Link to="/watching" className="storm__watchbar" aria-label={`${watch.event} also in effect — view details`}>
            <Eye size={14} className="storm__watchbar-icon" />
            <span className="storm__watchbar-text">
              <strong>{watch.event}</strong> also in effect
              {watchCountdown && watchCountdown !== 'expired' && (
                <span className="storm__watchbar-until"> · {watchCountdown} left</span>
              )}
            </span>
            <ChevronRight size={16} className="storm__watchbar-chev" />
          </Link>
        )}

        {/* IBW damage threat — sized/loudness by severity (driven by the params, not hard-coded). */}
        {hazards.damageThreat && (
          <div className={`storm__pds storm__pds--${dmgClass}`}>
            <Siren size={dmgClass === 'considerable' ? 14 : 17} />
            <span>
              {hazards.damageThreat} DAMAGE THREAT
              {(dmgClass === 'destructive' || dmgClass === 'catastrophic') && (
                <em className="storm__pds-sub">Particularly dangerous situation</em>
              )}
            </span>
          </div>
        )}

        <div className="storm__hero">
          <RadarMap
            lat={saved.lat}
            lon={saved.lon}
            geometry={warning.geometry}
            polygonColor={polyColor}
            motion={live.motion}
          />
        </div>

        <ImpactBlock eta={live.eta} motion={live.motion} />

        <section className="storm__panel">
          <h2 className="storm__panel-title">Hazards</h2>
          <div className="hazard-grid">
            {tornadoActive && (
              <Hazard
                icon={<Tornado size={20} />}
                tone="max"
                label="Tornado"
                value={
                  hazards.tornado === 'OBSERVED'
                    ? 'Confirmed'
                    : hazards.tornado === 'RADAR INDICATED'
                      ? 'Radar'
                      : 'Possible'
                }
              />
            )}
            {hazards.windGustMph != null && (
              <Hazard
                icon={<Wind size={20} />}
                label="Wind gust"
                value={`${hazards.windGustMph} mph`}
                note={hazards.windThreat === 'OBSERVED' ? 'Observed' : 'Radar'}
              />
            )}
            {hazards.hailInches != null && (
              <Hazard
                icon={<CloudHail size={20} />}
                label="Hail"
                value={hailLabel(hazards.hailInches) ?? `${hazards.hailInches}"`}
                note={hazards.hailThreat === 'OBSERVED' ? 'Observed' : 'Radar'}
              />
            )}
            {!tornadoActive && hazards.windGustMph == null && hazards.hailInches == null && (
              <p className="hazard-grid__none">Specific hazard tags weren’t included with this warning.</p>
            )}
          </div>
        </section>

        {/* Lightning — slot reserved; no live feed wired yet (we do NOT invent strikes). */}
        <section className="storm__panel">
          <h2 className="storm__panel-title">Lightning</h2>
          <div className="storm__lightning" role="note">
            <Zap size={18} />
            <div>
              <p className="storm__lightning-title">No live strike feed connected</p>
              <p className="storm__lightning-note">
                Strike count + proximity will appear here once a lightning data source (ENTLN / NLDN) is wired.
              </p>
            </div>
          </div>
        </section>

        <section className="storm__action">
          <div className="storm__action-icon">
            <ShieldAlert size={22} />
          </div>
          <div>
            <h2 className="storm__action-title">{action.headline}</h2>
            <p className="storm__action-body">{action.body}</p>
          </div>
        </section>

        <details className="storm__full">
          <summary>
            <span>Full warning text</span>
            <ExternalLink size={14} />
          </summary>
          <div className="storm__full-body">
            {warning.instruction && <p className="storm__full-instruction">{warning.instruction}</p>}
            <p>{warning.description}</p>
            <p className="storm__full-sender">Issued by {warning.senderName}</p>
          </div>
        </details>

        <Link to="/" className="storm__exit">Exit Storm Mode</Link>
      </div>
    </>
  )
}

/** Combined time-to-impact + distance + storm motion, as one block. */
function ImpactBlock({ eta, motion }: { eta: Eta; motion: StormMotion | null }) {
  let big: ReactNode
  const motionBits: string[] = []
  if (motion && motion.speedKt > 0) motionBits.push(`${compass(motion.towardDeg)} · ${motion.speedMph} mph`)
  if (eta.distanceMi != null) motionBits.push(`${eta.distanceMi} mi away`)

  switch (eta.status) {
    case 'now':
      big = <span className="impact__now">In your area now</span>
      break
    case 'approaching':
      big = (
        <>
          <span className="impact__lead">Impact in</span>
          <span className="impact__min num">~{eta.minutes}</span>
          <span className="impact__unit">min</span>
        </>
      )
      break
    case 'departing':
      big = <span className="impact__lead">Moving away</span>
      break
    case 'stationary':
      big = <span className="impact__lead">Nearly stationary</span>
      break
    default:
      big = <span className="impact__lead">Tracking storm</span>
  }

  return (
    <div className={`impact impact--${eta.status}`}>
      <div className="impact__top">
        <Clock size={16} className="impact__icon" />
        <div className="impact__text">{big}</div>
      </div>
      <div className="impact__motion">
        <Navigation size={13} />
        <span className="num">
          {eta.status === 'now'
            ? 'Take protective action immediately'
            : motionBits.length
              ? motionBits.join(' · ')
              : 'Motion data not available'}
        </span>
      </div>
    </div>
  )
}

function Hazard({
  icon,
  label,
  value,
  note,
  tone,
}: {
  icon: ReactNode
  label: string
  value: string
  note?: string
  tone?: 'max'
}) {
  return (
    <div className={`hazard${tone === 'max' ? ' hazard--max' : ''}`}>
      <span className="hazard__icon">{icon}</span>
      <span className="hazard__value num">{value}</span>
      <span className="hazard__label">{label}</span>
      {note && <span className="hazard__note">{note}</span>}
    </div>
  )
}
